import { AiOutlineTrophy } from 'react-icons/ai';
import FunctionCard from './FunctionCard';
import { PageContent, Page } from '@/components/custom/page';
import {
  MetricHat,
  MetricHeader,
  MetricHeaderLevel,
  MetricHeaderPoints,
  MetricHeaderStar,
  MetricHeaderTitle,
  MetricList,
  MetricNews,
  MetricNewsCard,
  MetricNewsCardCaption,
  MetricNewsCardContent,
  MetricNewsCardFigure,
  MetricNewsCardText,
  MetricNewsHeader,
  MetricNewsHeaderPoints,
  MetricNewsHeaderTitle,
  MetricSection
} from '@/components/custom/metric';
import { CardProgress, CardProgressBar, CardProgressContainer } from '@/components/ui/card';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { MedalContainer } from '@/components/custom/medal';
import { LevelProgressCard } from '@/components/custom/level-progress-card';
import { isPlayfulTheme } from '@/lib/layout-version';
import tiaConquistas from '@/assets/images/tia/TIA-COMEMORANDO.png';
import { TypewriterText } from '@/components/custom/typewriter-text';

export default function StudentMetricsView({ achievements, milestones, achievementRanking, user, isActive }) {
  const playful = isPlayfulTheme();
  // Barra de progresso: gradiente lúdico no infantil, verde padrão nos demais.
  const progressBarClass = playful ? 'bg-[image:var(--gradient-progress-bar)]' : 'bg-[#6FCF97]';
  const dailyAchievements = achievements?.filter(item => {
    if (item.achievement.category === 'daily') {
      return true;
    }
  });

  const weeklyAchievements = achievements?.filter(item => {
    if (item.achievement.category === 'weekly') {
      return true;
    }
  });
  const meritAchievements = achievements?.filter(item => {
    if (item.achievement.category === 'merit') {
      return true;
    }
  });

  const porcentagem = milestones?.['milestone-level']?.['points-required'] ? (milestones.score / milestones['next-milestone-level']['points-required']) * 100 : 0;

  return (
    <Page className="flex flex-col !flex-nowrap gap-16 xl:flex-row">
      <PageContent
        bg={!playful}
        className="gap-0"
      >
        {playful ? (
          <div className="relative mb-6 mt-16 flex flex-col gap-4 lg:flex-row lg:items-stretch">
            {/* T.I.A maior estourando o card (como na Home) + mensagem de incentivo */}
            <div
              className="relative flex flex-1 items-center rounded-[24px] p-4 pl-[150px] shadow-card"
              style={{ background: 'var(--color-bg-lilac)' }}
            >
              <img
                src={tiaConquistas}
                alt="T.I.A"
                className="pointer-events-none absolute bottom-0 left-2 z-10 h-[180px] w-auto object-contain object-bottom"
              />
              <div className="relative z-10 max-w-[800px] rounded-[16px] rounded-bl-none bg-white p-3.5 shadow-card">
                <p
                  className="mb-0 text-sm font-bold leading-relaxed"
                  style={{ color: '#222731' }}
                >
                  <TypewriterText text={'Cada missão completada te leva mais longe! Bora conquistar mais uma hoje? 🏆🚀'} />
                </p>
              </div>
            </div>

            {/* Card do aluno (info que vinha do banner cortado) */}
            <div className="flex items-center gap-3 rounded-[24px] bg-white p-4 shadow-card lg:w-[300px] lg:shrink-0">
              <AvatarWrapper
                size="md"
                src={user?.image?.url}
                text={user?.name}
                className="shrink-0 border-2 border-solid border-[var(--color-primary-lightest)]"
              />
              <div className="min-w-0">
                <p
                  className="mb-1 line-clamp-2 text-base font-extrabold leading-tight"
                  style={{ color: 'var(--color-secondary)' }}
                >
                  {user?.name}
                </p>
                <div className="flex flex-wrap gap-1">
                  <Badge
                    variant="bright"
                    size="sm"
                  >
                    {user?.['profile-names']?.[0]}
                  </Badge>
                  {isActive && (
                    <Badge
                      variant="success"
                      size="sm"
                    >
                      Ativo
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Card de Nível (mesmo componente compartilhado da Home). */}
            <LevelProgressCard
              milestones={milestones}
              className="lg:w-[340px] lg:shrink-0"
            />
          </div>
        ) : (
          <MetricHeader>
            <MetricHeaderLevel>
              <MetricHeaderTitle>
                <MetricHeaderStar isActive />
                Nível {milestones?.['milestone-level']?.['level-order'] ?? 1}
              </MetricHeaderTitle>
              <MetricHeaderTitle isDisabled>
                <MetricHeaderStar />
                Nível {milestones?.['next-milestone-level']?.['level-order'] ?? 2}
              </MetricHeaderTitle>
            </MetricHeaderLevel>

            <CardProgress className="w-full">
              <CardProgressContainer className="my-3">
                <CardProgressBar
                  className={progressBarClass}
                  percentage={porcentagem}
                />
              </CardProgressContainer>
            </CardProgress>

            <MetricHeaderPoints>
              <b>{milestones?.score ?? 0}</b>/{milestones?.['next-milestone-level']?.['points-required'] ?? 500} pontos
            </MetricHeaderPoints>
          </MetricHeader>
        )}

        <MetricList className={playful ? 'grid grid-cols-1 gap-4 lg:grid-cols-3 lg:items-start' : undefined}>
          <MetricSection
            variant="conquest"
            className={playful ? 'rounded-[20px] border bg-white shadow-card' : undefined}
          >
            <MetricHat>Conquistas Diárias</MetricHat>

            <MetricList space="sm">
              {dailyAchievements.map(item => {
                return (
                  <FunctionCard
                    title={item.achievement.title}
                    description={item.achievement.description}
                    done={item.done}
                    currentCount={item?.['stages-completed']}
                    totalCount={item.achievement?.['stages-count']}
                    points={item.achievement?.['base-score']}
                    buttonClass="achievements"
                  />
                );
              })}
            </MetricList>
          </MetricSection>

          <MetricSection
            variant="mission"
            className={playful ? 'rounded-[20px] border bg-white shadow-card' : undefined}
          >
            <MetricHat>Missões da semana</MetricHat>

            <MetricList space="sm">
              {weeklyAchievements.map(item => {
                return (
                  <FunctionCard
                    title={item.achievement.title}
                    description={item.achievement.description}
                    done={item.done}
                    currentCount={item?.['stages-completed']}
                    totalCount={item.achievement?.['stages-count']}
                    points={item.achievement?.['base-score']}
                    buttonClass="missions"
                  />
                );
              })}
            </MetricList>
          </MetricSection>

          <MetricSection
            variant="merit"
            className={playful ? 'rounded-[20px] border bg-white shadow-card' : undefined}
          >
            <MetricHat>Mérito</MetricHat>

            <MetricList space="sm">
              {meritAchievements.map(item => {
                return (
                  <FunctionCard
                    title={item.achievement.title}
                    description={item.achievement.description}
                    done={item.done}
                    currentCount={item?.['stages-completed']}
                    totalCount={item.achievement?.['stages-count']}
                    points={item.achievement?.['base-score']}
                    buttonClass="merit"
                  />
                );
              })}
            </MetricList>
          </MetricSection>
        </MetricList>
      </PageContent>

      {!playful && (
        <MetricNews>
          <MetricHat className="bg-primary-lightest text-primary">Evento mensal</MetricHat>

          <MetricNewsHeader>
            <MetricNewsHeaderTitle>{achievementRanking?.regional?.name}</MetricNewsHeaderTitle>

            <CardProgress className="w-full">
              <CardProgressContainer className="my-3">
                <CardProgressBar
                  className="bg-[#6FCF97]"
                  percentage={parseInt((achievementRanking?.['total-score'] * 100) / achievementRanking?.['required-score'])}
                />
              </CardProgressContainer>
            </CardProgress>

            <MetricNewsHeaderPoints>
              <span>
                {achievementRanking?.['total-score']} / {achievementRanking?.['required-score']} pts
              </span>
              <b>Ajude a GRE e ganhe 100pts</b>
            </MetricNewsHeaderPoints>
          </MetricNewsHeader>

          <MetricList space="sm">
            {achievementRanking?.['last-achievement-users'].map(item => {
              return (
                <MetricNewsCard>
                  <MetricNewsCardContent>
                    <MetricNewsCardFigure>
                      <AvatarWrapper
                        size="sm"
                        src={item?.user?.image?.url}
                        text={item?.user?.name}
                      />
                      <MetricNewsCardCaption>Nível {item?.['milestone-level']?.['level-order']}</MetricNewsCardCaption>
                    </MetricNewsCardFigure>

                    <MetricNewsCardText>
                      <b>{item?.user?.name}</b> ganhou <b>{item?.achievement?.['base-score']} pts</b> na missão <b>{item?.achievement?.['title']}</b>
                    </MetricNewsCardText>
                  </MetricNewsCardContent>

                  <MedalContainer
                    size="sm"
                    src="more"
                    value={<AiOutlineTrophy />}
                  />
                </MetricNewsCard>
              );
            })}
          </MetricList>
        </MetricNews>
      )}
    </Page>
  );
}
